import { useEffect, useRef, useState } from "react";
import { FormProvider, useForm, UseFormReturn } from "react-hook-form";
import { ClassNames } from "@emotion/react";
import styled from "@emotion/styled";
import { zodResolver } from "@hookform/resolvers/zod";
import { Spin } from "antd5";
import { Flex, Text } from "styles/utility-components";
import { z } from "zod";

import { UserInitialsType } from "components/comments/UserInitials";
import { TextArea } from "components/form_components/Inputs";
import Button from "components/ui/button/Button";
import { AiQuestionAndAnswer } from "lib/core_components/AiQuestionAndAnswer";
import { AiChatStreamResponse_Sources } from "lib/generated/app-service-gql/graphql";
import { useAiChatStreamSubscription } from "lib/hooks/api/ai_chat/useAiChatStreamSubscription";
import { useInitAiChat } from "lib/hooks/api/ai_chat/useInitAiChat";
import { sparklesFilled } from "lib/icons/CustomIcons";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { EventNames, useTracking } from "lib/tracking";
import { AiChatStreamResourceType } from "lib/types/graphQLEnums";
import AiChatHistory from "./AiChatHistory";
import AiChatSuggestedQuestionContainer from "./AiChatSuggestedQuestionContainer";
import { AiChatResourceType, AIQuestionForm, QaContent, SuggestedQuestion } from "./types";

type UiText = {
  title?: string;
  subtitle?: string;
  inputPlaceholder?: string;
};

const defaultText: UiText = {
  title: "Ask Stotles AI",
  subtitle: "Ask questions about anything from Stotles AI.",
  inputPlaceholder: "Ask anything",
};

function getResourceTypeEnum(resourceType: AiChatResourceType): AiChatStreamResourceType {
  switch (resourceType) {
    case "BUYER_QA":
      return AiChatStreamResourceType.BuyerQa;
    case "BID_ASK_AI":
      return AiChatStreamResourceType.BidAskAi;
    case "BID_WRITING_QA":
      return AiChatStreamResourceType.BidWritingQa;
  }
}

type AIChatProps = {
  resourceId: string;
  resourceName: string;
  resourceType: AiChatResourceType;
  additionalContext?: string;
  renderSaveQuestion?: () => JSX.Element;
  renderSources?: (sources: AiChatStreamResponse_Sources[]) => JSX.Element;
  renderInput?: (
    methods: UseFormReturn<AIQuestionForm>,
    handleQuestionSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>,
    isLoading: boolean,
  ) => JSX.Element;
  suggestedQuestions?: SuggestedQuestion[];
  showHistory?: boolean;
  showDisclaimer?: boolean;
  uiText?: UiText;
  suggestedQuestionsLayout?: "vertical" | "horizontal";
  userInitialsType?: UserInitialsType;
  mainContainerPadding?: string;
  chatThreadContainerPadding?: string;
};

/**
 * AiChatPanel component - UI for interacting with AI chat, handling questions, streaming responses, and history
 *
 * This panel can be used to build AI chat interfaces for various resources, such as buyers or bid asks. This can be
 * wrapped in AiChatModal to create a modal dialog for AI chat
 *
 * @param props.resourceId - ID of the resource to query
 * @param props.resourceName - Display name of the resource
 * @param props.resourceType - Category of AI chat for context
 * @param props.additionalContext - Additional context to provide to the AI chat
 * @param props.renderSaveQuestion - Optional render function for a save question button
 * @param props.renderSources - Optional render function for displaying sources
 * @param props.renderInput - Optional render function for displaying custom input field. Has access to react-hook-form methods and handleQuestionSubmit function
 * @param props.suggestedQuestions - List of suggested questions to display when no conversation
 * @param props.showHistory - Whether to show conversation history panel
 * @param props.showDisclaimer - Whether to show disclaimer message at the bottom of the chat
 * @param props.uiText - Override text for title, subtitle, and input placeholder
 * @param props.suggestedQuestionsLayout - Layout of suggested questions, either "vertical" or "horizontal". The default is "horizontal"
 * @param props.userInitialsType - Type of user initials to display in the chat (default, small, or extra-small)
 * @param props.mainContainerPadding - Padding for the main container. Defaults to "16px 48px 16px 32px"
 * @param props.chatThreadContainerPadding - Padding for the chat thread container. Defaults to "48px 16px 0 0". If changing, better to leave space in the right for the scrollbar
 */
export default function AiChatPanel({
  resourceId,
  resourceName,
  resourceType,
  additionalContext,
  renderSaveQuestion,
  renderSources,
  renderInput,
  suggestedQuestions = [],
  showHistory = true,
  showDisclaimer = true,
  uiText,
  suggestedQuestionsLayout,
  userInitialsType,
  mainContainerPadding,
  chatThreadContainerPadding,
}: AIChatProps) {
  const [selectedConversationId, setSelectedConversationId] = useState<string | undefined>();
  const containerRef = useRef<HTMLDivElement>(null);
  const { logEvent } = useTracking();

  const methods = useForm<AIQuestionForm>({
    mode: "onChange",
    defaultValues: {
      conversations: [],
      userInputQuestion: "",
    },
    resolver: zodResolver(
      z.object({
        userInputQuestion: z.string().min(10),
      }),
    ),
  });

  const { control, handleSubmit, setValue, getValues, watch, formState } = methods;

  const { conversations } = watch();

  const currentConversation = conversations.find(
    (c) => c.conversationId === selectedConversationId,
  );

  const {
    isLoading: isStartingAiChatSubscription,
    mutate: startAiChatSubscription,
    reset: resetAiChatSubscription,
  } = useInitAiChat({
    onSuccess: (data) => {
      const { question, conversationId } = data.initAiChat;
      onNewQuestionAsked(conversationId, question);
    },
  });

  // Reset the subscription when the component unmounts
  useEffect(() => {
    return () => {
      resetAiChatSubscription();
    };
  }, [resetAiChatSubscription]);

  const { data: subscriptionData } = useAiChatStreamSubscription(
    currentConversation?.conversationId,
    {
      enabled: Boolean(currentConversation?.conversationId && window.currentUser),
      onData(data, errors) {
        if (errors && errors.length > 0) {
          console.error("AI chat subscription error:", errors);
          return;
        }

        const { conversationId, answer, sources } = data?.onAiChatResponse || {};
        handleSubscriptionAnswerUpdate(conversationId, answer, sources);
      },
    },
  );

  const scrollToBottomOfAiChat = () => {
    setTimeout(() => {
      containerRef.current?.lastElementChild?.scrollIntoView({ behavior: "smooth" });
    }, 500);
  };

  /**
   * Starts the {@see useAiChatStreamSubscription} subscription by setting
   * currentConversationId and appending the new question to the react hook form state
   *
   * @param conversationId conversationId within whose context this question & answer pair exist
   * @param questionAsked the question just asked by the user
   */
  const onNewQuestionAsked = (conversationId: string, questionAsked: string) => {
    const newQaContent: QaContent = {
      question: questionAsked,
      answer: "",
    };

    setSelectedConversationId(conversationId);

    const convIndex = conversations.findIndex((c) => c.conversationId === conversationId);
    if (convIndex === -1) {
      // New conversation — add entire conversation with first Q&A
      setValue("conversations", [
        ...conversations,
        {
          conversationId,
          questionAndAnswer: [newQaContent],
        },
      ]);
    } else {
      // Append to existing conversation if ID exists
      const updatedQA = [...conversations[convIndex].questionAndAnswer, newQaContent];
      setValue(`conversations.${convIndex}.questionAndAnswer`, updatedQA);
    }

    // Set the question input field to empty
    setValue("userInputQuestion", "");

    // Scroll to the bottom of the question list when question is asked
    scrollToBottomOfAiChat();
  };

  const handleSubscriptionAnswerUpdate = (
    conversationId: string,
    answer: string | null | undefined,
    sources?: AiChatStreamResponse_Sources[] | null,
  ) => {
    const currentConversations = getValues("conversations");
    const currentConversationIndex = currentConversations.findIndex(
      (c) => c.conversationId === conversationId,
    );
    const conversation = currentConversations[currentConversationIndex];

    // If the conversation ID is somehow not found in the form, or the answer field is blank, do nothing
    if (currentConversationIndex === -1 || !answer || conversation.questionAndAnswer.length === 0)
      return;

    const lastQAIndex = conversation.questionAndAnswer.length - 1;

    setValue(
      `conversations.${currentConversationIndex}.questionAndAnswer.${lastQAIndex}.answer`,
      answer,
    );

    // The sources should come through when the answers are complete
    if (sources?.length) {
      setValue(
        `conversations.${currentConversationIndex}.questionAndAnswer.${lastQAIndex}.sources`,
        sources,
      );
    }
  };

  const handleQuestionSubmit = handleSubmit(({ userInputQuestion }) => {
    // clear the previous subscription before streaming a new response
    // otherwise, if the socket ever disconnects (like when the browser tab has been idle for a while)
    // the subscription will not be restarted and the user will see like the question is not working
    resetAiChatSubscription();
    logEvent(EventNames.newQuestionAsked, {
      "Chat name": userInputQuestion,
      "Resource Id": resourceId,
      "Resource name": resourceName,
      "Context source": "Ask AI",
    });

    startAiChatSubscription({
      req: {
        question: userInputQuestion,
        resourceId,
        resourceType: getResourceTypeEnum(resourceType),
        conversationId: currentConversation?.conversationId,
        context: additionalContext,
      },
    });
  });

  const { title, subtitle, inputPlaceholder } = { ...defaultText, ...uiText };
  const isLoading = isStartingAiChatSubscription || subscriptionData?.isComplete === false;

  return (
    <AiChatForm onSubmit={handleQuestionSubmit}>
      <Flex height="100%">
        {showHistory && (
          <FormProvider {...methods}>
            <AiChatHistory
              resourceId={resourceId}
              resourceName={resourceName}
              resourceType={resourceType}
              onNewChatClick={() => {
                setSelectedConversationId(undefined);
                resetAiChatSubscription();
                logEvent(EventNames.newChatCreated, {
                  "Context source": "Ask AI",
                  "Resource Id": resourceId,
                  "Resource name": resourceName,
                });
              }}
              selectedConversationId={selectedConversationId}
              setSelectedConversationId={(id) => setSelectedConversationId(id)}
            />
          </FormProvider>
        )}
        <MainContainer padding={mainContainerPadding}>
          {!currentConversation ? (
            <Flex column justifyContent="space-between" height="100%">
              <EmptyThreadPlaceholder>
                {title && <UIcon svg={sparklesFilled} size={40} />}
                <Text h2 center>
                  {title}
                </Text>
                <Text center>{subtitle}</Text>
              </EmptyThreadPlaceholder>

              {/* Render suggested questions if there are no Q&A pairs */}
              <SuggestedQuestionsContainer layout={suggestedQuestionsLayout}>
                {!isLoading &&
                  suggestedQuestions.length > 0 &&
                  suggestedQuestions.map((q) => (
                    <AiChatSuggestedQuestionContainer
                      {...q}
                      key={q.title}
                      resourceId={resourceId}
                      resourceName={resourceName}
                      onClick={() => {
                        setValue("userInputQuestion", q.prompt);
                        void handleQuestionSubmit();
                      }}
                    />
                  ))}
              </SuggestedQuestionsContainer>
            </Flex>
          ) : (
            <ChatThreadContainer ref={containerRef} padding={chatThreadContainerPadding}>
              {/* Render current Q&A pairs for conversation */}
              {currentConversation.questionAndAnswer.map(({ question, answer, sources }, index) => (
                <div key={index}>
                  <AiQuestionAndAnswer
                    question={question}
                    answer={answer.replace(/\\n/g, "\n")}
                    saveQuestionComponent={renderSaveQuestion && renderSaveQuestion()}
                    type={userInitialsType}
                  />

                  {/* Render additional data if available */}
                  {sources && renderSources && renderSources(sources)}
                </div>
              ))}

              {/* Used to scroll to the bottom of the question list */}
              <span style={{ visibility: "hidden" }} />
            </ChatThreadContainer>
          )}
          <Flex column gap={16}>
            {renderInput ? (
              renderInput(methods, handleQuestionSubmit, isLoading)
            ) : (
              <Flex alignItems="center" gap={8}>
                <ClassNames>
                  {({ css }) => {
                    const placeholder =
                      inputPlaceholder && inputPlaceholder?.length > 100
                        ? inputPlaceholder.slice(0, 100) + "..."
                        : inputPlaceholder;
                    return (
                      <TextArea
                        autoSize={{ maxRows: 5 }}
                        autoFocus
                        name="userInputQuestion"
                        label={""}
                        control={control}
                        className={css({
                          width: "100%",
                          borderRadius: 24,
                        })}
                        placeholder={placeholder}
                        disabled={isLoading}
                        aria-label="Ask a question"
                        showErrors={false}
                        onPressEnter={(e) => {
                          if (e.shiftKey) {
                            // Allow newline when Shift + Enter is pressed
                            return;
                          }
                          // Prevent newline and trigger submission on Enter without Shift
                          e.preventDefault();
                          e.stopPropagation();
                          void handleQuestionSubmit();
                        }}
                      />
                    );
                  }}
                </ClassNames>
                {isLoading ? (
                  <Spin />
                ) : (
                  <Button
                    buttonType="primary"
                    disabled={Boolean(formState.errors.userInputQuestion)}
                    type="submit"
                    aria-label="Ask question"
                    icon="arrowUp"
                    iconProps={{ size: 16 }}
                    style={{ borderRadius: "50%" }}
                  />
                )}
              </Flex>
            )}
            {showDisclaimer && (
              <Text description center>
                AI-generated responses may be inaccurate. Verify important information.
              </Text>
            )}
          </Flex>
        </MainContainer>
      </Flex>
    </AiChatForm>
  );
}

const AiChatForm = styled.form({
  height: "100%",
});

const MainContainer = styled.div<{ padding?: string }>(({ padding = "16px 48px 16px 32px" }) => ({
  display: "flex",
  flexDirection: "column",
  backgroundColor: "white",
  justifyContent: "space-between",
  borderRadius: 8,
  width: 1800,
  flexGrow: 1,
  padding,
}));

const ChatThreadContainer = styled(Flex)<{ padding?: string }>(({ padding = "48px 16px 0 0" }) => ({
  flexDirection: "column",
  padding,
  marginBottom: 16,
  gap: 32,
  overflowY: "auto",
}));

const SuggestedQuestionsContainer = styled.div<{
  layout?: "vertical" | "horizontal";
}>(({ layout = "horizontal" }) => ({
  display: "grid",
  marginBottom: 16,
  gridTemplateColumns: layout === "horizontal" ? "1fr 1fr" : "1fr",
  gap: 16,
}));

const EmptyThreadPlaceholder = styled(Flex)({
  marginTop: 48,
  flexDirection: "column",
  gap: 16,
  alignItems: "center",
});
